import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export type ScoutPopoverSession = {
  conversationId: string | undefined;
  setConversationId: (id: string | undefined) => void;
  openedAt: Date;
};

/**
 * Shared session state for in-context Scout conversations.
 *
 * Held outside the popover so the popover can unmount (e.g. when the user
 * minimizes to the bottom-of-screen bar) without losing the active thread.
 * Resets on navigation: a question about a facility shouldn't carry into the
 * next page's question about a chemical. Search params and hash changes are
 * ignored so tab/filter changes within a single record don't reset.
 */
export function useScoutPopoverSession(): ScoutPopoverSession {
  const { pathname } = useLocation();
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [openedAt, setOpenedAt] = useState<Date>(() => new Date());

  useEffect(() => {
    setConversationId(undefined);
    setOpenedAt(new Date());
  }, [pathname]);

  const setConversationIdStable = useCallback((id: string | undefined) => {
    setConversationId(id);
  }, []);

  return {
    conversationId,
    setConversationId: setConversationIdStable,
    openedAt,
  };
}
